import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, Circle as LeafletCircle, Polygon, Popup, useMapEvents } from 'react-leaflet';
import { OpenStreetMapTiles } from './OpenStreetMapTiles';
import { MapPin, Plus, X, Send, Trash2, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { POPULATION_ZONES, densityToOpacity, densityToLabel } from '../lib/populationZones';
import { unionAreaSqMi, maxRadiusWithinBudget } from '../lib/zone-area';
import { zoneSyncError, zoneSyncMessage } from '../lib/zone-sync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Must stay in sync with backend `_validate_mini_zones` in server.py.
const MAX_MINI_ZONES = 9;
const TOTAL_AREA_BUDGET_SQMI = 30;
const DEFAULT_ZONE_RADIUS = 1.0;
const MIN_ZONE_RADIUS = 0.25;
const MAX_ZONE_RADIUS = 3.0;
const COLUMBUS_FALLBACK = { lat: 40.0, lng: -83.0 };

const ZONE_AREA_TOLERANCE_SQMI = 0.1;

// Overlap is charged once, so the budget is measured on the union of the circles.
const zoneArea = (r) => Math.PI * (Number(r) || 0) * (Number(r) || 0);
const totalArea = (zones) => unionAreaSqMi(zones);

// Click-to-add helper (mirrors LeadForm.jsx pattern).
const MapClickHandler = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
};

export const PremiumZonesPanel = ({ centerLat, centerLng, onZonesSaved }) => {
  const [zones, setZones] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null); // {kind:'success'|'error', text}

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('advecti_token')}`,
    'Content-Type': 'application/json',
  });

  const mapCenter = useMemo(() => {
    const lat = Number(centerLat);
    const lng = Number(centerLng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return [COLUMBUS_FALLBACK.lat, COLUMBUS_FALLBACK.lng];
  }, [centerLat, centerLng]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/zones`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setZones((d.mini_zones || []).map((z) => ({ ...z })));
      }
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const areaUsed = totalArea(zones);
  const areaRemaining = Math.max(0, TOTAL_AREA_BUDGET_SQMI - areaUsed);
  const areaPct = Math.min(100, (areaUsed / TOTAL_AREA_BUDGET_SQMI) * 100);
  const overBudget = areaUsed > TOTAL_AREA_BUDGET_SQMI + ZONE_AREA_TOLERANCE_SQMI;
  const nearBudget = areaUsed > TOTAL_AREA_BUDGET_SQMI * 0.9;
  const zoneCount = zones.length;
  const atMaxZones = zoneCount >= MAX_MINI_ZONES;

  const update = (i, field, value) => {
    const next = [...zones];
    next[i] = { ...next[i], [field]: value };
    setZones(next);
    setDirty(true);
  };

  const updateRadius = (i, newRadius) => {
    const next = [...zones];
    const capByBudget = maxRadiusWithinBudget(next, i, next[i], TOTAL_AREA_BUDGET_SQMI, MAX_ZONE_RADIUS);
    const capped = Math.min(Number(newRadius) || 0, MAX_ZONE_RADIUS, capByBudget);
    next[i] = { ...next[i], radius: Math.max(MIN_ZONE_RADIUS, Number(capped.toFixed(3))) };
    setZones(next);
    setDirty(true);
  };

  const addZoneAt = (lat, lng) => {
    setMsg(null);
    if (atMaxZones) {
      setMsg({ kind: 'error', text: `Maximum ${MAX_MINI_ZONES} premium zones reached.` });
      return;
    }
    const candidate = { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
    const maxByBudget = maxRadiusWithinBudget(zones, -1, candidate, TOTAL_AREA_BUDGET_SQMI, MAX_ZONE_RADIUS);
    if (maxByBudget < MIN_ZONE_RADIUS) {
      setMsg({ kind: 'error', text: 'No area budget remaining. Shrink or remove a zone first.' });
      return;
    }
    const radius = Math.min(DEFAULT_ZONE_RADIUS, maxByBudget);
    setZones((prev) => [...prev, {
      name: `Zone ${prev.length + 1}`,
      lat: candidate.lat,
      lng: candidate.lng,
      radius: Number(radius.toFixed(3)),
    }]);
    setDirty(true);
  };

  const addBlankZone = () => addZoneAt(mapCenter[0], mapCenter[1]);
  const removeZone = (i) => { setZones(zones.filter((_, idx) => idx !== i)); setDirty(true); };
  const clearAll = () => { setZones([]); setDirty(true); };

  const save = async () => {
    if (overBudget) {
      setMsg({ kind: 'error', text: `Over budget — total ${areaUsed.toFixed(2)} mi² exceeds the ${TOTAL_AREA_BUDGET_SQMI} mi² cap.` });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/zones`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          mini_zones: zones.map((z) => ({
            name: z.name, lat: parseFloat(z.lat), lng: parseFloat(z.lng), radius: parseFloat(z.radius),
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setZones((data.mini_zones || []).map((z) => ({ ...z })));
        setDirty(false);
        setMsg({ kind: 'success', text: 'Zones saved. Click "Sync zones to app" to push them to your drivers.' });
        onZonesSaved && onZonesSaved(data.mini_zones || []);
      } else {
        setMsg({ kind: 'error', text: data.detail || 'Save failed' });
      }
    } catch {
      setMsg({ kind: 'error', text: 'Network error — try again.' });
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    if (syncing) return;
    if (dirty) {
      setMsg({ kind: 'error', text: 'Save your changes first, then sync.' });
      return;
    }
    setSyncing(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/zones/sync`, { method: 'POST', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(zoneSyncMessage(data));
      } else {
        setMsg({ kind: 'error', text: zoneSyncError(data) });
      }
    } catch {
      setMsg({ kind: 'error', text: 'Connection interrupted; the sync result is unknown. Check the driver campaign before retrying.' });
    } finally {
      setSyncing(false);
    }
  };

  const barColor = overBudget ? '#dc2626' : nearBudget ? '#ea580c' : '#1e3a5f';

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/10" data-testid="premium-zones-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-bold text-navy flex items-center gap-2">
          <MapPin size={18} /> Premium Driving Zones
        </h3>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={save} disabled={busy || overBudget}
                    className="px-4 py-2 text-sm font-semibold bg-navy text-cream rounded-lg disabled:opacity-50"
                    data-testid="zones-panel-save">
              {busy ? 'Saving…' : 'Save zones'}
            </button>
          )}
          <button onClick={sync} disabled={syncing || busy || dirty || zoneCount === 0 || overBudget}
                  aria-busy={syncing}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50"
                  data-testid="zones-panel-sync">
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {syncing ? 'Syncing…' : 'Sync zones to app'}
          </button>
        </div>
      </div>
      <p className="text-xs text-navy/50 mb-3">
        Click the map to drop a new zone (or edit coordinates below). The navy heatmap shows population density —
        darker = more people to see your wrap. Save, then push the update to the driver app.
      </p>

      {/* 30 mi² budget bar */}
      <div className="mb-3" data-testid="zones-area-budget">
        <div className="flex justify-between text-xs text-navy/70 mb-1 font-semibold">
          <span data-testid="zones-area-used">
            {areaUsed.toFixed(2)} / {TOTAL_AREA_BUDGET_SQMI} mi² used
          </span>
          <span data-testid="zones-area-remaining">
            {zoneCount}/{MAX_MINI_ZONES} zones · {areaRemaining.toFixed(2)} mi² remaining
          </span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${areaPct}%`, backgroundColor: barColor }}
            data-testid="zones-area-bar"
          />
        </div>
        {overBudget && (
          <p className="text-xs text-red-600 mt-1 font-semibold" data-testid="zones-over-budget-msg">
            Over the {TOTAL_AREA_BUDGET_SQMI} mi² cap. Shrink or remove a zone to save.
          </p>
        )}
      </div>

      {msg && (
        <p role="status" aria-live="polite" className={`text-sm mb-3 break-words ${msg.kind === 'success' ? 'text-green-700' : msg.kind === 'warning' ? 'text-amber-700' : 'text-red-600'}`} data-testid="zones-panel-msg">
          {msg.text}
        </p>
      )}

      {/* Clickable map */}
      <div className="rounded-xl overflow-hidden border-2 border-navy/15 mb-4" style={{ height: 340 }} data-testid="premium-zones-map">
        <MapContainer
          center={mapCenter}
          zoom={10}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <OpenStreetMapTiles mapId="premium-zones-map" />
          {/* Population density heatmap (context — darker = more populated). Non-interactive. */}
          {POPULATION_ZONES.map((z) => (
            <Polygon
              key={`pop-${z.name}`}
              positions={z.polygon}
              pathOptions={{
                color: '#1e3a5f',
                fillColor: '#1e3a5f',
                fillOpacity: densityToOpacity(z.density),
                weight: 0.5,
                opacity: 0.4,
                interactive: true,
              }}
            >
              <Popup>
                <div style={{ minWidth: 160 }}>
                  <p style={{ margin: 0, fontWeight: 700, color: '#1e3a5f' }}>{z.name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#555' }}>
                    {z.density.toLocaleString()} people/sq mi · <em>{densityToLabel(z.density)}</em>
                  </p>
                </div>
              </Popup>
            </Polygon>
          ))}
          <MapClickHandler onClick={({ lat, lng }) => addZoneAt(lat, lng)} />
          {zones.map((z, i) => {
            const lat = Number(z.lat), lng = Number(z.lng), r = Number(z.radius);
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(r)) return null;
            return (
              <LeafletCircle
                key={`pz-${i}`}
                center={[lat, lng]}
                radius={r * 1609.34}
                pathOptions={{ color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.25, weight: 2 }}
              />
            );
          })}
        </MapContainer>
      </div>

      {zoneCount === 0 && (
        <p className="text-sm text-navy/50 italic mb-2">
          No premium zones yet — click anywhere on the map to add your first one, or use &quot;Add zone&quot; below.
        </p>
      )}

      {/* Per-zone editable rows */}
      <div className="space-y-2">
        {zones.map((z, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 bg-cream/40 rounded-lg p-2">
            <span className="text-xs font-bold text-navy/60 w-6" data-testid={`my-zone-index-${i}`}>#{i + 1}</span>
            <input type="text" value={z.name || ''} placeholder={`Zone ${i + 1}`}
                   onChange={(e) => update(i, 'name', e.target.value)}
                   className="px-3 py-2 border border-navy/20 rounded-lg text-sm w-40"
                   data-testid={`my-zone-name-${i}`} />
            <input type="number" step="any" value={z.lat} placeholder="Latitude"
                   onChange={(e) => update(i, 'lat', e.target.value)}
                   className="px-3 py-2 border border-navy/20 rounded-lg text-sm w-32"
                   data-testid={`my-zone-lat-${i}`} />
            <input type="number" step="any" value={z.lng} placeholder="Longitude"
                   onChange={(e) => update(i, 'lng', e.target.value)}
                   className="px-3 py-2 border border-navy/20 rounded-lg text-sm w-32"
                   data-testid={`my-zone-lng-${i}`} />
            <div className="flex items-center gap-1">
              <input type="number" step="0.05" min={MIN_ZONE_RADIUS} max={MAX_ZONE_RADIUS}
                     value={z.radius ?? DEFAULT_ZONE_RADIUS}
                     onChange={(e) => updateRadius(i, e.target.value)}
                     className="px-3 py-2 border border-navy/20 rounded-lg text-sm w-20"
                     data-testid={`my-zone-radius-${i}`} />
              <span className="text-xs text-navy/40">mi</span>
              <span className="text-xs text-navy/50 ml-1 tabular-nums" data-testid={`my-zone-area-${i}`}>
                ({zoneArea(z.radius).toFixed(2)} mi²)
              </span>
            </div>
            {z.lat && z.lng && (
              <a href={`https://www.google.com/maps?q=${z.lat},${z.lng}`} target="_blank" rel="noreferrer"
                 className="text-xs text-navy/50 underline">map</a>
            )}
            <button onClick={() => removeZone(i)}
                    className="text-red-500 hover:bg-red-50 p-1.5 rounded ml-auto"
                    data-testid={`my-zone-remove-${i}`}>
              <X size={15} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={addBlankZone}
                disabled={atMaxZones}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-navy border border-navy/20 rounded-lg hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="my-zone-add">
          <Plus size={14} /> Add zone
        </button>
        {zoneCount > 0 && (
          <button onClick={clearAll}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                  data-testid="my-zones-clear-all">
            <Trash2 size={14} /> Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export default PremiumZonesPanel;
